import { supabase } from '@/lib/supabase';
import { Class } from '@/types/classTypes';
import { handleError, ErrorSeverity } from '@/utils/errorHandler';

export interface ClassCreateData {
  name: string;
  description?: string;
  level?: string;
  max_students?: number;
  dress_code?: string;
  location_id?: string; // Reference to locations table
  price_cents?: number;
  billing_model?: string;
  organization_id: string;
  is_event?: boolean;
  event_date?: string;
  event_end_date?: string;
  event_start_time?: string;
  event_end_time?: string;
  season_id?: string;
  category?: string;
  status?: 'active' | 'inactive' | 'draft' | 'archived';
  trial_available?: boolean;
  is_active?: boolean;
}

export interface ClassUpdateData {
  name?: string;
  description?: string;
  level?: string;
  max_students?: number;
  dress_code?: string;
  location_id?: string; // Reference to locations table
  price_cents?: number;
  billing_model?: string;
  is_event?: boolean;
  event_date?: string | null;
  event_end_date?: string | null;
  event_start_time?: string | null;
  event_end_time?: string | null;
  is_active?: boolean;
  season_id?: string | null;
  category?: string;
  status?: 'active' | 'inactive' | 'draft' | 'archived';
  trial_available?: boolean;
  makeup_policy?: 'flexible' | 'curated' | null;
}

export const ClassMutationService = {
  /**
   * Create a new class
   */
  async createClass(classData: ClassCreateData & { schedules?: any[] }): Promise<Class | null> {
    try {
      // Extract schedules if present
      const { schedules, ...classFields } = classData;
      const { data, error } = await supabase
        .from('classes')
        .insert([classFields])
        .select('*')
        .single();
      if (error) throw error;
      // Upsert schedules if provided
      if (schedules && schedules.length > 0) {
        const scheduleRows = schedules.map(s => ({
          class_id: data.id,
          organization_id: data.organization_id,
          weekday: s.weekday,
          start_time: s.start_time,
          end_time: s.end_time,
          name: s.name || null,
        }));
        const { error: scheduleError } = await supabase
          .from('class_schedules')
          .upsert(scheduleRows, { onConflict: 'class_id,weekday,start_time,end_time' });
        if (scheduleError) throw scheduleError;
      }
      return data as Class;
    } catch (error) {
      handleError(
        error, 
        "Failed to create class", 
        ErrorSeverity.ERROR,
        { operation: 'createClass', additionalInfo: { classData } }
      );
      return null;
    }
  },

  /**
   * Update an existing class
   */
  async updateClass(classId: string, updates: ClassUpdateData & { schedules?: any[] }): Promise<Class | null> {
    try {
      // Extract schedules if present
      const { schedules, ...classFields } = updates;
      const { data, error } = await supabase
        .from('classes')
        .update(classFields)
        .eq('id', classId)
        .select('*')
        .single();
      if (error) throw error;
      // Update schedules if provided
      if (schedules !== undefined) {
        // Remove all existing schedules for this class (to handle deletions)
        await supabase.from('class_schedules').delete().eq('class_id', classId);
        
        // Insert new schedules if any
        if (schedules.length > 0) {
          const scheduleRows = schedules.map(s => ({
            class_id: classId,
            organization_id: data.organization_id,
            weekday: s.weekday,
            start_time: s.start_time,
            end_time: s.end_time,
            name: s.name || null,
          }));
          
          const { error: scheduleError } = await supabase
            .from('class_schedules')
            .insert(scheduleRows);
          if (scheduleError) throw scheduleError;
        }
      }
      return data as Class;
    } catch (error) {
      handleError(
        error, 
        "Failed to update class", 
        ErrorSeverity.ERROR,
        { operation: 'updateClass', additionalInfo: { classId, updates } }
      );
      return null;
    }
  },

  /**
   * Delete a class
   */
  async deleteClass(classId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('classes')
        .delete()
        .eq('id', classId);
      
      if (error) throw error;
      return true;
    } catch (error) {
      handleError(
        error, 
        "Failed to delete class", 
        ErrorSeverity.ERROR,
        { operation: 'deleteClass', additionalInfo: { classId } }
      );
      return false;
    }
  }
};
